import React from 'react';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { CardGridProps } from './CardGrid.types';

/**
 * Page scaffold for card-grid screens. Renders a heading + subtitle, an optional error
 * alert, a responsive grid (1/2/3/4 columns) of the supplied cells, and an optional
 * empty state. When `loading` is set it renders only a centered full-page spinner.
 */
export default function CardGrid({
    title,
    subtitle,
    error,
    onDismissError,
    loading = false,
    empty = false,
    emptyTitle = 'Nothing here yet',
    emptyMessage,
    minHeight = false,
    children,
}: CardGridProps): React.ReactElement {
    if (loading) {
        return (
            <Container fluid className="vh-100 d-flex align-items-center justify-content-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container fluid className={`py-4 px-4${minHeight ? ' min-vh-100' : ''}`}>
            <Row className="mb-4">
                <Col>
                    <h2 className="fw-bold">{title}</h2>
                    {subtitle && <p className="text-muted">{subtitle}</p>}
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert
                            variant="danger"
                            dismissible={Boolean(onDismissError)}
                            onClose={onDismissError}
                        >
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
                {children}
            </Row>

            {empty && (
                <Row className="mt-5">
                    <Col className="text-center">
                        <div className="text-muted">
                            <h4>{emptyTitle}</h4>
                            {emptyMessage && <p>{emptyMessage}</p>}
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    );
}
